import BaseApi from '@lumapps/base-api';
import { get } from '@lumapps/constants';
import { organizationExtensionApi } from '@lumapps/marketplace/api';
import { AppId } from '@lumapps/router';

import type {
    ListMicroAppsParams,
    MicroAppApi,
    MicroAppProps,
    MicroAppsContextVariables,
    MicroAppsInputVariables,
    RunMicroAppResponse,
    SubmitRunMicroAppParams,
} from '../types';

const config = get();

const LONG_TIMEOUT = 60000;
const PATH = 'micro-apps';

/**
 *  Api call for embedded app
 */
export const embeddedMicroAppApi = new BaseApi({
    path: '',
    baseURL: '/v2/embedded/organizations',
    version: BaseApi.versions.v2,
});

const getMicroAppApiClient = () => {
    if (config.applicationId === AppId.embedded || window.APP_ID === AppId.embedded) {
        return {
            client: embeddedMicroAppApi,
            axiosConfig: { withCredentials: true },
            path: `/${config.customerId}/extensions`,
        };
    }

    return { client: organizationExtensionApi, path: '', axiosConfig: { withCredentials: false } };
};

/**
 * Get the given micro-app.
 */
export const getMicroApp = async (extensionId: string, signal?: AbortSignal) => {
    const { data: microApp } = await organizationExtensionApi.get<MicroAppApi>(`/${extensionId}/microapp`, {
        signal,
    });

    return microApp;
};

/**
 * Run the given micro-app.
 * No runId is provided, so a new micro-app is created.
 */
export const runMicroApp = async (
    extensionId: string,
    signal?: AbortSignal,
    inputVariables?: MicroAppsInputVariables,
    contextVariables?: MicroAppsContextVariables,
) => {
    const { client, path, axiosConfig } = getMicroAppApiClient();
    const { data } = await client.post<RunMicroAppResponse>(
        `${path}/${extensionId}/microapp/runs`,
        { inputVariables, contextVariables },
        // adding long timeout to axios request is necessary here because some APIs are slow
        { signal, timeout: LONG_TIMEOUT, ...axiosConfig },
    );
    return data;
};

/**
 * Submit the given micro-app
 * A runId is provided so an existing micro-app is used.
 */
export const submitRunMicroApp = async ({
    action,
    microAppId: extensionId,
    contextVariables,
    runId,
}: SubmitRunMicroAppParams) => {
    const { data: inputs, id: submittedActionId, actionType, skipFormValidation } = action;

    const { client, path, axiosConfig } = getMicroAppApiClient();
    const { data } = await client.post<RunMicroAppResponse>(
        `${path}/${extensionId}/microapp/runs`,
        {
            [actionType]: {
                inputs,
                submittedActionId,
                skipFormValidation,
            },
            contextVariables,
            runId,
        },
        // adding long timeout to axios request is necessary here because some APIs are slow
        { timeout: LONG_TIMEOUT, ...axiosConfig },
    );
    return data;
};

/** Keys for all micro app queries */
export const microAppsQueryKeys = {
    all: [PATH] as const,
    detail: (extensionId: string) => [...microAppsQueryKeys.all, { extensionId }] as const,
    getMicroApp: (microAppId: string) => [...microAppsQueryKeys.all, { microAppId }] as const,
    listMicroApps: (params: ListMicroAppsParams) => ['installed-extensions', params] as const,
    runMicroApp: (
        microAppId?: MicroAppProps['id'],
        runInstanceId?: MicroAppProps['runInstanceId'],
        inputVariables?: MicroAppsInputVariables,
        contextVariables?: MicroAppsContextVariables,
    ) => {
        return [
            'extensions',
            microAppId,
            'microapp',
            'runs',
            runInstanceId,
            'variables',
            inputVariables,
            contextVariables,
        ] as const;
    },
};
